import { useEffect, useMemo, useState } from 'react';
import { LifecycleStatusTag } from 'components/lifecycle-status-tag';
import { Search, InlineLoading, Button } from '@carbon/react';
import { CheckmarkFilled, CloseFilled, Rocket } from '@carbon/react/icons';
import { SidePanel } from '@carbon/ibm-products';
import { useAtom, useSetAtom } from 'jotai';
import { formatMutationResource } from 'utils/misc';
import { formatTcWorkspaceDate } from 'utils/dates';
import type { PreDrillWorkspaceListRow } from 'types/api-responses';
import 'components/inline-edit/ibm-products-config';
import {
  selectWellboreViewWorkspacesAtom,
  wellboreGeometryWorkspaceSelectionsAtom,
} from './atoms';
import { pickDefaultWorkspace } from './utils';
import styles from './select-workspace-panel.module.scss';

export interface SelectWorkspacePanelProps {
  open: boolean;
  /** Permit wellbore UWI14 — drives the workspaces list query. */
  uwi14: string;
  /** Self href of the workspace currently driving the bore — pre-selects it. */
  currentWorkspaceHref?: string;
  onClose: () => void;
  /** Fired on Apply with the chosen uwi14 so the caller re-fetches /queries/wells. */
  onApplied?: (uwi14: string) => void;
}

// Subsurface Explorer panel to choose which promoted pre-drill workspace drives
// a permit bore's geometry. Unlike Edit Source, Apply only records the selection
// (`wellboreGeometryWorkspaceSelectionsAtom`) for the caller's `/queries/wells` re-fetch
// — it does NOT POST `…/default_selections`, so no server-side default changes.
export const SelectWorkspacePanel = ({
  open,
  uwi14,
  currentWorkspaceHref,
  onClose,
  onApplied,
}: SelectWorkspacePanelProps) => {
  const [listResource, fetchList] = useAtom(selectWellboreViewWorkspacesAtom);
  const setWorkspaceSelections = useSetAtom(
    wellboreGeometryWorkspaceSelectionsAtom
  );
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { loading, data } = formatMutationResource(listResource);
  const rows = useMemo<PreDrillWorkspaceListRow[]>(
    () => data?.results ?? [],
    [data]
  );

  useEffect(() => {
    if (!open) return;
    setQuery('');
    fetchList({
      type: 'POST',
      url: '/queries/wellbore_geometry_workspaces',
      data: { query: { uwi14: [uwi14], promoted: true } },
    });
  }, [open, uwi14, fetchList]);

  // SidePanel pushes #main-content via inline margin; clear it on unmount (we
  // unmount while open, so SidePanel's own open→false cleanup never runs).
  useEffect(
    () => () => {
      const pageContent = document.querySelector('#main-content');
      if (pageContent instanceof HTMLElement) {
        pageContent.style.marginInlineEnd = '';
        pageContent.style.marginInlineStart = '';
        pageContent.style.inlineSize = '';
      }
    },
    []
  );

  // Default selection: the bore's current driving workspace, else the default
  // (isDefault, or the latest-created when none is flagged default).
  useEffect(() => {
    if (!open) return;
    const current =
      rows.find((r) => r.href === currentWorkspaceHref) ??
      pickDefaultWorkspace(rows);
    setSelectedId(current ? current.id : null);
  }, [open, rows, currentWorkspaceHref]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(needle));
  }, [query, rows]);

  const currentId =
    rows.find((r) => r.href === currentWorkspaceHref)?.id ??
    pickDefaultWorkspace(rows)?.id ??
    null;
  const canApply = selectedId !== null && selectedId !== currentId;

  const handleApply = () => {
    if (selectedId === null) return;
    const selected = rows.find((r) => r.id === selectedId);
    const selectedUwi14 = selected?.uwi14 ?? uwi14;
    // Record uwi14 → workspace id only — the wells query reads this to drive the
    // geometry. We intentionally do NOT POST `…/default_selections` here.
    setWorkspaceSelections((prev) => ({
      ...prev,
      [selectedUwi14]: selectedId,
    }));
    onApplied?.(selectedUwi14);
    onClose();
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Select Wellbore View"
      subtitle={`${uwi14} (Permit)`}
      className="pre-drill-view-source-sidepanel"
      slideIn
      selectorPageContent="#main-content"
      placement="right"
      size="md"
    >
      <div className={styles.body}>
        <p className={styles.description}>
          Select the workspace that generated the geometry for this wellbore.
          The selected workspace drives the analysis and subsurface explorer
          visualizations.
        </p>
        <Search
          size="md"
          labelText="Search workspaces"
          placeholder="Search input text"
          value={query}
          onChange={(e) => setQuery(e.target.value ?? '')}
        />
        <div className={styles.list}>
          {loading ? (
            <InlineLoading description="Loading workspaces…" />
          ) : filtered.length === 0 ? (
            <div className={styles.empty}>No workspaces found</div>
          ) : (
            filtered.map((row) => {
              const isSelected = row.id === selectedId;
              return (
                <button
                  type="button"
                  key={row.id}
                  className={`${styles.row}${
                    isSelected ? ` ${styles.rowSelected}` : ''
                  }`}
                  onClick={() => setSelectedId(row.id)}
                  aria-pressed={isSelected}
                >
                  <span className={styles.rowMain}>
                    <span className={styles.rowTitle}>
                      <span className={styles.rowName}>{row.name}</span>
                      {row.isDefault && <LifecycleStatusTag status="default" />}
                    </span>
                    {row.createdBy?.displayName && (
                      <span className={styles.rowCreator}>
                        {row.createdBy.displayName}
                      </span>
                    )}
                    <span className={styles.rowDates}>
                      Created: {formatTcWorkspaceDate(row.createdAt)} | Updated:{' '}
                      {formatTcWorkspaceDate(row.updatedAt)}
                    </span>
                    {(row.workspaceVerdict === 'correct' ||
                      row.workspaceVerdict === 'incorrect' ||
                      row.promoted) && (
                      <span className={styles.statusRow}>
                        {row.workspaceVerdict === 'correct' && (
                          <span className={styles.statusCorrect}>
                            <CheckmarkFilled size={16} aria-hidden />
                            Correct
                          </span>
                        )}
                        {row.workspaceVerdict === 'incorrect' && (
                          <span className={styles.statusIncorrect}>
                            <CloseFilled size={16} aria-hidden />
                            Incorrect
                          </span>
                        )}
                        {row.promoted && (
                          <span className={styles.statusPromoted}>
                            <Rocket size={16} aria-hidden />
                            Promoted
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <CheckmarkFilled className={styles.rowCheck} />
                  )}
                </button>
              );
            })
          )}
        </div>
        <div className={styles.actions}>
          <Button kind="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" disabled={!canApply} onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </SidePanel>
  );
};
